import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';

const loginSchema = z.object({
  email: z.string().email('Email no válido'),
  password: z.string().min(1, 'La contraseña es obligatoria'),
});

type LoginForm = z.infer<typeof loginSchema>;

export function Login() {
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginForm) => { await login(data); };

  return (
    <div style={{ minHeight: '100vh', display: 'grid', gridTemplateColumns: '1fr 1fr', fontFamily: "'Inter', sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
        * { box-sizing: border-box; }
        .login-input { width: 100%; padding: 12px 16px; border: 1.5px solid #e2e8f0; border-radius: 10px; font-size: .95rem; color: #1e293b; outline: none; transition: border-color .2s, box-shadow .2s; font-family: 'Inter', sans-serif; background: #f8fafc; }
        .login-input:focus { border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59,130,246,.1); background: white; }
        .login-input.error { border-color: #ef4444; }
        .login-label { display: block; font-size: .82rem; font-weight: 600; color: #475569; margin-bottom: 6px; }
        .login-error { font-size: .78rem; color: #ef4444; margin-top: 4px; }
        .login-btn { width: 100%; padding: 14px; background: #3b82f6; color: white; border: none; border-radius: 10px; font-size: 1rem; font-weight: 700; cursor: pointer; transition: background .2s, transform .15s; font-family: 'Inter', sans-serif; }
        .login-btn:hover:not(:disabled) { background: #2563eb; transform: translateY(-1px); }
        .login-btn:disabled { background: #93c5fd; cursor: not-allowed; }
        @media (max-width: 768px) { .login-left { display: none !important; } .login-right { grid-column: 1 / -1 !important; } }
      `}</style>

      {/* LEFT */}
      <div className="login-left" style={{ background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 48px', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at 70% 80%, rgba(59,130,246,.2) 0%, transparent 60%)' }}></div>
        <div style={{ position: 'relative', zIndex: 1, textAlign: 'center', maxWidth: 400 }}>
          <div style={{ fontSize: '3.5rem', marginBottom: 24 }}>🏋️</div>
          <h2 style={{ color: 'white', fontSize: '2rem', fontWeight: 800, marginBottom: 16, lineHeight: 1.2 }}>
            Bienvenido de vuelta
          </h2>
          <p style={{ color: '#94a3b8', fontSize: '.95rem', lineHeight: 1.7, marginBottom: 40 }}>
            Gestiona tus eventos, clientes y analítica desde un solo panel profesional.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[
              { icon: '📅', text: 'Eventos y clases publicados' },
              { icon: '📊', text: 'Dashboard con métricas reales' },
              { icon: '🌐', text: 'Tu página pública activa' },
            ].map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'rgba(255,255,255,.06)', borderRadius: 12, padding: '12px 16px', border: '1px solid rgba(255,255,255,.08)' }}>
                <span style={{ fontSize: '1.1rem' }}>{item.icon}</span>
                <span style={{ color: '#e2e8f0', fontSize: '.88rem', fontWeight: 500 }}>{item.text}</span>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 40, padding: '20px 24px', background: 'rgba(59,130,246,.1)', borderRadius: 16, border: '1px solid rgba(59,130,246,.2)' }}>
            <p style={{ color: '#93c5fd', fontSize: '.85rem', fontStyle: 'italic', lineHeight: 1.6 }}>
              "FitPulse cambió cómo gestiono mi gimnasio. Ahora todo está en un solo lugar."
            </p>
            <p style={{ color: '#64748b', fontSize: '.75rem', marginTop: 8 }}>— Carlos R., Iron Club Madrid</p>
          </div>
        </div>
      </div>

      {/* RIGHT */}
      <div className="login-right" style={{ background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 24px' }}>
        <div style={{ width: '100%', maxWidth: 400 }}>

          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <a href="https://fitpulse.es" style={{ textDecoration: 'none' }}>
              <span style={{ fontSize: '1.4rem', fontWeight: 800, color: '#3b82f6' }}>● Fit</span>
              <span style={{ fontSize: '1.4rem', fontWeight: 800, color: '#1e293b' }}>Pulse</span>
            </a>
            <h1 style={{ fontSize: '1.6rem', fontWeight: 800, color: '#1e293b', marginTop: 16, marginBottom: 6 }}>
              Iniciar sesión
            </h1>
            <p style={{ color: '#64748b', fontSize: '.88rem' }}>
              ¿No tienes cuenta?{' '}
              <Link to="/register" style={{ color: '#3b82f6', fontWeight: 600, textDecoration: 'none' }}>Registrarse gratis</Link>
            </p>
          </div>

          <div style={{ background: 'white', borderRadius: 20, padding: '32px', boxShadow: '0 4px 24px rgba(0,0,0,.06)', border: '1px solid #e2e8f0' }}>
            <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>

              <div>
                <label className="login-label">Email</label>
                <input {...register('email')} type="email" className={`login-input ${errors.email ? 'error' : ''}`} placeholder="tu@email.com" />
                {errors.email && <p className="login-error">{errors.email.message}</p>}
              </div>

              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <label className="login-label" style={{ margin: 0 }}>Contraseña</label>
                  <Link to="/forgot-password" style={{ fontSize: '.78rem', color: '#3b82f6', textDecoration: 'none', fontWeight: 600 }}>¿Olvidaste tu contraseña?</Link>
                </div>
                <div style={{ position: 'relative' }}>
                  <input {...register('password')} type={showPassword ? 'text' : 'password'} className={`login-input ${errors.password ? 'error' : ''}`} placeholder="••••••••" style={{ paddingRight: 44 }} />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 0 }}>
                    {showPassword ? <EyeSlashIcon style={{ width: 18 }} /> : <EyeIcon style={{ width: 18 }} />}
                  </button>
                </div>
                {errors.password && <p className="login-error">{errors.password.message}</p>}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" id="remember" style={{ flexShrink: 0 }} />
                <label htmlFor="remember" style={{ fontSize: '.82rem', color: '#64748b' }}>Recordarme</label>
              </div>

              <button type="submit" disabled={isSubmitting} className="login-btn">
                {isSubmitting ? '⏳ Entrando...' : '→ Iniciar sesión'}
              </button>

            </form>
          </div>

          <p style={{ textAlign: 'center', marginTop: 20, fontSize: '.78rem', color: '#94a3b8' }}>
            🔒 Conexión segura con SSL
          </p>
        </div>
      </div>
    </div>
  );
}
